import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { routesMap } from "@/components/routes/routes";
import freeDomainList from "@/statics/freeEmailDomains.json";
import {
  doPlanSignup,
  doTrialSignup,
} from "@/services/service-layer/authService";
import OpticksCheckbox from "@/components/ui/OpticksCheckbox/OpticksCheckbox";
import OpticksButton from "@/components/ui/OpticksButton/OpticksButton";
import OpticksInput from "@/components/ui/OpticksInput/OpticksInput";
import OpticksLink from "@/components/ui/OpticksLink/OpticksLink";
import type { SignupFormPayload } from "@/types/model/Auth";
import { getTimezone } from "@/utilities/utils";
import usePaymentChargebee from "@/components/hooks/use-payment-chargebee/use-payment-chargebee";
import { PLAN_MODE, SELFSERVICE_PAID_PLANS } from "@/utilities/constants";
import { planIsFreeTrial } from "@/utilities/planHelpers";
import OpticksLogo from "@/components/ui/OpticksLogos/OpticksLogo";
import OpticksSpinner from "@/components/ui/OpticksSpinner/OpticksSpinner";
import Spinner from "@/components/ui/Spinner/Spinner";
import ChevronDownIcon from "@/components/ui/OpticksIcons/ChevronDownIcon";
import { Controller, useForm } from "react-hook-form";
import { useAuthStore } from "@/zustand/authStore";
import { useLoaderStore } from "@/zustand/loaderStore";
import OpticksPlanPillIndicator from "@/components/ui/OpticksPlanPillIndicator/OpticksPlanPillIndicator";
import GoogleAuthButton from "@/components/ui/GoogleAuthButton/GoogleAuthButton";
import storageService from "@/services/middleware/storageService";
import "./SignupPlan.css";

const SignupPlan = () => {
  const [signupFields, setSignupFields] = useState<SignupFormPayload>({
    email: "",
    firstName: "",
    companyName: "",
    password: "",
    terms: false,
    timezone: "",
    jobTitle: "",
    context: {
      pageUri: "",
      pageName: "",
    },
  });
  const { loading: isLoading, setLoading } = useLoaderStore((state) => state);
  const { validUser = false } = useAuthStore((state) => state.auth);
  const { openCheckout, paymentStep } = usePaymentChargebee();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get("plan") || SELFSERVICE_PAID_PLANS.LITE;
  const currency = searchParams.get("currency") || "EUR";
  const frequency = searchParams.get("frequency") || "monthly";
  const numVisitsAddons = searchParams.get("numVisitsAddons") || 0;
  const referrerParam = searchParams.get("referrer");
  const referrer = (() => {
    if (referrerParam) {
      storageService.setItem("referrer", referrerParam);
      return referrerParam;
    }
    return storageService.getItem("referrer") || undefined;
  })();
  const isFreeTrial = planIsFreeTrial(plan);

  useEffect(() => {
    if (validUser && plan !== PLAN_MODE.FREE) {
      openCheckout(
        plan as "free_trial" | "lite" | "pro" | "max",
        currency as string,
        frequency as string,
        Number(numVisitsAddons) as number,
        signupFields,
        true
      );
    }
  }, [validUser]);

  const doSignup = (values: SignupFormPayload) => {
    setLoading(true);
    const timezone = getTimezone();
    if (plan === "free_trial") {
      doTrialSignup({
        ...values,
        timezone,
        referrer,
        context: {
          pageUri: window.location.href,
          pageName: "Free Register",
        },
        hitId: window.optHitId,
        sid: document.cookie
          .split("; ")
          .find((row) => row.startsWith("opticksid="))
          ?.replace("opticksid=", ""),
      });
    } else {
      setSignupFields({
        ...values,
        timezone,
        referrer,
        context: {
          pageUri: window.location.href,
          pageName: "Tier Register", // TODO Review this
        },
      });
      doPlanSignup({
        ...values,
        timezone,
        referrer,
        context: {
          pageUri: window.location.href,
          pageName: "Tier Register", // TODO Review this
        },
      });
    }
  };

  const PlanLogo = () => {
    switch (plan) {
      case PLAN_MODE.FREE:
        return <OpticksPlanPillIndicator planType={PLAN_MODE.FREE} />;
      case SELFSERVICE_PAID_PLANS.LITE:
        return (
          <OpticksPlanPillIndicator planType={SELFSERVICE_PAID_PLANS.LITE} />
        );
      case SELFSERVICE_PAID_PLANS.PRO:
        return (
          <OpticksPlanPillIndicator planType={SELFSERVICE_PAID_PLANS.PRO} />
        );
      case SELFSERVICE_PAID_PLANS.MAX:
        return (
          <OpticksPlanPillIndicator planType={SELFSERVICE_PAID_PLANS.MAX} />
        );
      default:
        return (
          <OpticksPlanPillIndicator planType={SELFSERVICE_PAID_PLANS.LITE} />
        );
    }
  };

  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitting },
  } = useForm<SignupFormPayload>({
    mode: "onChange",
    defaultValues: {
      email: "",
      firstName: "",
      companyName: "",
      password: "",
      terms: false,
      timezone: "",
      jobTitle: "",
      context: {
        pageUri: "",
        pageName: "",
      },
    },
  });

  if (!validUser) {
    return (
      <>
        <h2>
          Sign up to <OpticksLogo />
          <div className="plan-pill-with-link">
            <PlanLogo />
            <OpticksButton
              type="button"
              variation="ghost"
              size="s"
              label="More plans"
              iconTrailing={<ChevronDownIcon size={14} />}
              onClick={() =>
                document
                  .getElementById("plan-selector-anchor")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="see-plans-pill"
            />
          </div>
        </h2>
        <div>{paymentStep}</div>
        <form
          className="auth-form"
          noValidate={true}
          onSubmit={handleSubmit(doSignup)}
        >
          <div className="fields">
            <Controller
              name="email"
              control={control}
              rules={{
                required: "Required",
                validate: (value) => {
                  if (!/\S+@\S+\.\S{2,}/.test(value)) {
                    return "Invalid";
                  }
                  if (freeDomainList.includes(value.split("@")[1] ?? "")) {
                    return "Please enter a valid business email";
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <div>
                  <OpticksInput
                    placeholder="Email"
                    type="email"
                    required
                    surface="white"
                    input={field}
                    className="sign-up-email"
                  />
                  {errors.email && (
                    <span className="form-invalid-message">
                      {errors.email.message}
                    </span>
                  )}
                </div>
              )}
            />

            <Controller
              name="firstName"
              control={control}
              rules={{ required: "Required" }}
              render={({ field }) => (
                <div>
                  <OpticksInput
                    placeholder="First name"
                    type="text"
                    required
                    surface="white"
                    input={field}
                    className="sign-up-email"
                  />
                  {errors.firstName && (
                    <span className="form-invalid-message">
                      {errors.firstName.message}
                    </span>
                  )}
                </div>
              )}
            />

            <Controller
              name="companyName"
              control={control}
              rules={{ required: "Required" }}
              render={({ field }) => (
                <div>
                  <OpticksInput
                    placeholder="Company name"
                    type="text"
                    required
                    surface="white"
                    input={field}
                    className="sign-up-email"
                  />
                  {errors.companyName && (
                    <span className="form-invalid-message">
                      {errors.companyName.message}
                    </span>
                  )}
                </div>
              )}
            />

            <Controller
              name="password"
              control={control}
              rules={{
                required: "Required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters",
                },
                maxLength: {
                  value: 16,
                  message: "Password must be no more than 16 characters",
                },
              }}
              render={({ field }) => (
                <div>
                  <OpticksInput
                    placeholder="Password"
                    type="password"
                    required
                    surface="white"
                    input={field}
                    className="sign-up-email"
                  />
                  {errors.password && (
                    <span className="form-invalid-message">
                      {errors.password.message}
                    </span>
                  )}
                </div>
              )}
            />

            <Controller
              name="terms"
              control={control}
              rules={{
                validate: (value) => value === true || "Required",
              }}
              render={({ field }) => (
                <div>
                  <OpticksCheckbox
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  >
                    <span>
                      I accept the{" "}
                      <OpticksLink
                        href="https://optickssecurity.com/resources/files/Opticks-Terms-and-Conditions-for-Self-Service-v3.pdf"
                        label="terms and conditions"
                      />
                    </span>
                  </OpticksCheckbox>

                  {errors.terms && (
                    <span className="form-invalid-message">
                      {errors.terms.message}
                    </span>
                  )}
                </div>
              )}
            />
          </div>

          <div className="login-btn">
            {!isLoading ? (
              <OpticksButton
                type="submit"
                size="m-wide"
                disabled={!isValid || isSubmitting}
                label="Sign Up"
              />
            ) : (
              <Spinner size={40} />
            )}
          </div>

          {isFreeTrial && (
            <>
              <div className="signup-plan-divider">
                <span>or</span>
              </div>
              <GoogleAuthButton text="signup_with" />
            </>
          )}

          <div className="login-account">
            Already have an account?{" "}
            <Link to={routesMap.auth.login.path}>Login</Link>
          </div>
        </form>
      </>
    );
  }
  if (paymentStep.toLocaleLowerCase() === "error") {
    return (
      <>
        <h2>
          Sign up to <OpticksLogo /> <PlanLogo />
        </h2>
        <div>{paymentStep}</div>

        <div className="login-btn">
          <OpticksButton
            onClick={() =>
              openCheckout(
                plan as "free_trial" | "lite" | "pro" | "max",
                currency as string,
                frequency as string,
                Number(numVisitsAddons) as number,
                signupFields,
                true
              )
            }
            size="m-wide"
            label="Reload Payment"
          />
        </div>
      </>
    );
  }
  return (
    <>
      <h2>Getting everything ready</h2>
      <OpticksSpinner />
    </>
  );
};

export default SignupPlan;
